/*
 * Linked price fields (Base Pricing card and each unit's Pricing tab):
 *
 *   Base price  -> tax = 5% of it (until the tax is typed by hand), total = base + tax,
 *                  discount % worked out again from the original price
 *   Tax         -> as typed (total updates)
 *   Total       -> base price + tax split back out (unit tab only)
 *   Original    -> discount % = (original - base) / original
 *   Discount %  -> original = base / (1 - discount)
 *
 * The original (crossed-out) price is what guests see struck through;
 * the base price is what they pay before tax.
 */

export const DEFAULT_TAX_RATE = 5; // %

const num = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};
const round = (n) => Math.round(n);
const round1 = (n) => Math.round(n * 10) / 10;

export const autoTax = (actual) => round((num(actual) * DEFAULT_TAX_RATE) / 100);

// a % tax saved on older plans is left alone
const isAmountTax = (pricing) => !pricing?.taxType || pricing.taxType === 'fixed';

// tax still equals what we'd fill in (or is empty) -> keep filling it in
const taxIsAuto = (pricing) => {
    const tax = num(pricing?.tax);
    return isAmountTax(pricing) && (tax === 0 || tax === autoTax(pricing?.actual));
};

export const discountFrom = (original, actual) =>
    num(original) > 0 && num(actual) > 0 && num(original) > num(actual)
        ? round1(((num(original) - num(actual)) / num(original)) * 100)
        : 0;

export const originalFrom = (actual, discount) =>
    num(actual) > 0 && num(discount) > 0 && num(discount) < 100 ? round(num(actual) / (1 - num(discount) / 100)) : 0;

/** pricing after one field changes (returns a new object; empty input = 0) */
export function linkPricing(pricing = {}, field, raw) {
    const value = raw === '' || raw === null || raw === undefined ? 0 : num(raw);
    const next = { ...pricing };

    if (field === 'actual') {
        const auto = taxIsAuto(pricing);
        next.actual = value;
        if (auto) next.tax = autoTax(value);
    } else if (field === 'tax') {
        next.tax = value;
    } else if (field === 'total') {
        if (taxIsAuto(pricing)) {
            next.actual = round(value / (1 + DEFAULT_TAX_RATE / 100));
            next.tax = value - next.actual;
        } else {
            next.actual = Math.max(0, value - num(pricing.tax));
        }
    } else if (field === 'higherPrice') {
        next.higherPrice = value;
        next.discountPercent = discountFrom(value, next.actual);
        next.total = num(next.actual) + num(next.tax);
        return next;
    } else if (field === 'discountPercent') {
        const discount = Math.min(95, Math.max(0, value));
        next.discountPercent = discount;
        next.higherPrice = originalFrom(next.actual, discount);
        next.total = num(next.actual) + num(next.tax);
        return next;
    } else {
        next[field] = raw;
        return next;
    }

    // base price changed: keep the original price, work the discount out again
    // (no original yet but a discount: work the original out instead)
    if (num(next.higherPrice) > 0) next.discountPercent = discountFrom(next.higherPrice, next.actual);
    else if (num(next.discountPercent) > 0) next.higherPrice = originalFrom(next.actual, next.discountPercent);
    next.total = num(next.actual) + num(next.tax);
    return next;
}

// hint under the original price when it isn't above the base price
export const originalPriceWarning = (pricing) =>
    num(pricing?.higherPrice) > 0 && num(pricing?.actual) > 0 && num(pricing.higherPrice) <= num(pricing.actual)
        ? 'Original price should be more than the base price.'
        : '';
